/**
 * StockToggle — "Только в наличии" / "Бар гана" (STORY-003-005; restyled
 * 2026-08).
 *
 * A single accessible toggle (`role="switch"` + `aria-checked`) mapping to the
 * backend `in_stock_only=true` query param (AC #3 → `stock_state IN
 * ('in_stock','low')`). Hand-rolled: `@radix-ui/react-switch` is not a
 * dependency and adding one for a two-state control would be a poor trade.
 *
 * The whole row is the hit target (the `<label>` wraps both the text and the
 * switch), which on a phone is the difference between a comfortable tap and a
 * 44×24 sliver. The off state now sits on `--g-rule-strong` rather than
 * `--g-hairline`: at 8% ink the old track was invisible against the card in
 * light mode, so "off" and "not rendered" looked identical.
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";

export interface StockToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export function StockToggle({ checked, onChange }: StockToggleProps): ReactElement {
  const { t } = useTranslation("marketplace");
  return (
    <label className="flex min-h-[44px] cursor-pointer items-center justify-between gap-3 text-sm font-medium text-foreground">
      <span>{t("filters.inStockOnly")}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={t("filters.inStockOnly")}
        data-testid="stock-toggle"
        onClick={() => onChange(!checked)}
        className={
          "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background " +
          (checked
            ? "bg-[color:var(--g-primary)]"
            : "bg-[color:var(--g-rule-strong)]")
        }
      >
        <span
          aria-hidden="true"
          className={
            "inline-block size-5 rounded-full bg-[color:var(--g-surface)] shadow-[var(--g-shadow-sm)] transition-transform motion-reduce:transition-none " +
            (checked ? "translate-x-5" : "translate-x-0.5")
          }
        />
      </button>
    </label>
  );
}
